import { BoardAPI } from '@business/api/board';
import { Blocs } from '@business/blocs';
import { BoardResponse, ProjectId } from '@business/entities/project';
import { StageId } from '@business/entities/project/ProjectStageResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, map } from 'rxjs/operators';
import { R } from '@core/utils/r';

export class BoardBloc extends BaseBloc {
  private readonly boardRepo = new Repository<BoardResponse>({
    getItemId: board => board.id,
    pagenated: true,
  });

  readonly boards$ = this.boardRepo.items$.pipe(map(boards => R.sortBy(boards, b => b.name)));

  readonly selectBoard = this.boardRepo.selectItem;

  onReset() {
    this.boardRepo.reset();
  }

  fetchBoards = (permission = false) => {
    return BoardAPI.fetchBoards(permission).pipe(this.boardRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_board_error') }));
  };

  fetchBoard = (id: string, status = 'active', permission = false) => {
    return BoardAPI.fetchBoard(id, status, permission).pipe(
      this.boardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_board_error') }),
    );
  };

  createBoard = (data: Partial<BoardResponse>) => {
    return BoardAPI.createBoard(data).pipe(
      this.boardRepo.ops.addOne(item => item),
      switchMap(() => resolve(Blocs.settings).fetchSettings()),
      handleMessage({
        type: requestMessage('create_board'),
      }),
    );
  };

  updateBoard = (id: string, data: Partial<BoardResponse>) => {
    return BoardAPI.updateBoard(id, data).pipe(
      this.boardRepo.ops.upsertOne(item => ({ item })),
      switchMap(() => resolve(Blocs.settings).fetchSettings()),
      handleMessage({
        type: requestMessage('update_board'),
      }),
    );
  };

  deleteBoard = (id: string) => {
    return showDeleteConfirmation('Delete Board', 'Do you really want to remove this board?').pipe(
      switchMap(() => BoardAPI.deleteBoard(id)),
      this.boardRepo.ops.removeOne(() => id),
      switchMap(() => resolve(Blocs.settings).fetchSettings()),
      handleMessage({
        type: requestMessage('delete_board'),
      }),
    );
  };

  orderProjects = (stageId: StageId, projects: ProjectId[]) => {
    return BoardAPI.orderProjects(stageId, projects).pipe(
      //   this.boardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('project_order_error'),
      }),
    );
  };
}
